import React, {FC} from "react";
import {InputNumber, InputNumberProps} from "antd";

interface ICustomInputNumber extends InputNumberProps {
  onlyDigits?: boolean;
}

const formatWithSpaces = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const stringValue = typeof value === "number" ? value.toString() : value;
  const [integerPart, decimalPart] = stringValue.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

const sanitizeDigits = (value: string) => value.replace(/\D/g, "");

export const CustomInputNumber: FC<ICustomInputNumber> = ({
  onlyDigits = false,
  formatter,
  parser,
  onKeyDown,
  onPaste,
  ...rest
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (onlyDigits && !(event.ctrlKey || event.metaKey || event.altKey)) {
      if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
        event.preventDefault();
        if (onKeyDown) {
          onKeyDown(event);
        }
        return;
      }
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (onlyDigits) {
      const pastedText = event.clipboardData.getData("text");
      if (!/^\d+$/.test(pastedText)) {
        event.preventDefault();
        return;
      }
    }

    if (onPaste) {
      onPaste(event);
    }
  };

  const mergedFormatter =
    formatter ??
    ((value) => {
      if (value === undefined || value === null || value === "") {
        return "";
      }

      if (onlyDigits) {
        const sanitized = sanitizeDigits(`${value}`);
        return formatWithSpaces(sanitized);
      }

      return formatWithSpaces(value);
    });

  const mergedParser =
    parser ??
    ((value) => {
      if (value === undefined || value === null || value === "") {
        return undefined as unknown as number;
      }

      const withoutSpaces = value.replace(/\s/g, "");
      const normalized = onlyDigits ? sanitizeDigits(withoutSpaces) : withoutSpaces;

      if (!normalized) {
        return undefined as unknown as number;
      }

      return Number(normalized) as unknown as number;
    });

  return (
    <InputNumber
      {...rest}
      formatter={mergedFormatter}
      parser={mergedParser}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  );
};